import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { DoodleIcon } from "./DoodleIcon";
import { testimonials as originalTestimonials } from "../data/mock";
import stepikTestimonials from "../data/stepikTestimonials.json";
import "./review-sources.css";
const allTestimonials = [...originalTestimonials, ...stepikTestimonials];
const testimonials = [
  ...allTestimonials.filter(review => review.id === '543975'),
  ...allTestimonials.filter(review => review.id !== '543975'),
];

export const TestimonialsSection = () => {
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const sheets = useRef([]);
  const playing = useRef([]);
  const locked = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      playing.current.forEach(animation => animation.cancel());
    };
  }, []);

  const turn = async (direction, event) => {
    if (locked.current) return;
    const nextIndex = (index + direction + testimonials.length) % testimonials.length;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || event.detail === 0) {
      setIndex(nextIndex);
      return;
    }
    locked.current = true;
    setBusy(true);
    const top = sheets.current[index];
    const easing = 'cubic-bezier(0.23, 1, 0.32, 1)';
    const duration = 280;
    const poses = ['translate3d(0,0,0) rotate(0deg)', 'rotate(3deg) translate3d(0,4px,0)', 'rotate(-5deg) translate3d(0,8px,0)'];
    const pose = offset => poses[Math.min(offset, 2)];
    const offsetFor = (sheetIndex, activeIndex) => (sheetIndex - activeIndex + testimonials.length) % testimonials.length;
    const moving = [];
    // Read the current transforms before changing layers, including reverse turns.
    const starts = sheets.current.map((sheet, sheetIndex) => offsetFor(sheetIndex, index) > 2 ? pose(2) : window.getComputedStyle(sheet).transform);
    sheets.current.forEach((sheet, sheetIndex) => {
      if (sheet === top) return;
      const before = offsetFor(sheetIndex, index);
      const after = offsetFor(sheetIndex, nextIndex);
      if (before > 2 && after > 2) return;
      sheet.style.visibility = 'visible';
      sheet.style.zIndex = String(after === 0 ? 3 : after === 1 ? 2 : 1);
      moving.push(sheet.animate([
        { transform: starts[sheetIndex], opacity: before > 2 ? 0 : 1 },
        { transform: pose(after), opacity: after > 2 ? 0 : 1 }
      ], { duration, easing, fill: 'forwards' }));
    });
    top.style.zIndex = '4';
    // Clear the stack completely before changing z-index; the sheets never pop
    // through each other. Both halves belong to one compositor timeline.
    const away = `translate3d(${direction * 108}%, -3%, 0) rotate(${direction * 5}deg)`;
    const leave = top.animate([
      { transform: starts[index], offset: 0, easing: 'cubic-bezier(0.77, 0, 0.175, 1)' },
      { transform: away, offset: .5, easing: 'cubic-bezier(0.77, 0, 0.175, 1)' },
      { transform: pose(offsetFor(index, nextIndex)), offset: 1 }
    ], { duration, easing: 'linear', fill: 'forwards' });
    const layer = top.animate([
      { zIndex: '4', offset: 0, easing: 'steps(1, end)' },
      { zIndex: '0', offset: .5 },
      { zIndex: '0', offset: 1 }
    ], { duration, fill: 'forwards' });
    playing.current = [leave, layer, ...moving];
    try {
      await Promise.all(playing.current.map(animation => animation.finished));
      // Commit the matching resting poses before removing the animation fill.
      // A normal batched setState would expose the previous poses for a frame.
      if (mounted.current) flushSync(() => setIndex(nextIndex));
    } catch {
      // Unmount cancels the running sheet animations.
    } finally {
      playing.current.forEach(animation => animation.cancel());
      playing.current = [];
      sheets.current.forEach(sheet => {
        if (!sheet) return;
        sheet.style.zIndex = '';
        sheet.style.visibility = '';
      });
      locked.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  return <section id="testimonials" className="editorial-section editorial-testimonials">
    <div className="editorial-container editorial-split">
      <div>
        <h2>Отзывы<br /><span className="ink-underline">студентов</span></h2>
        <div className="review-controls">
          <button aria-label="Предыдущий отзыв" disabled={busy} onClick={event => turn(-1, event)}><ArrowLeft size={24}/></button>
          <span aria-live="polite">{String(index + 1).padStart(2, '0')} / {String(testimonials.length).padStart(2, '0')}</span>
          <button aria-label="Следующий отзыв" disabled={busy} onClick={event => turn(1, event)}><ArrowRight size={24}/></button>
        </div>
      </div>
      <div className="review-paper-stack" aria-busy={busy}>
        <span className="paper-tape" aria-hidden="true"/>
        {testimonials.map((review, reviewIndex) => {
          const offset = (reviewIndex - index + testimonials.length) % testimonials.length;
          const layer = ['front', 'next', 'bottom'][offset] || 'hidden';
          return <figure key={reviewIndex} ref={element => { sheets.current[reviewIndex] = element; }} className={`editorial-quote review-sheet-${layer}`} aria-hidden={offset !== 0} aria-live={offset === 0 ? 'polite' : undefined}>
            <span className="paper-quote-mark" aria-hidden="true">“</span>
            <div className="review-stars" aria-label={`Оценка ${review.rating} из 5`}>{Array.from({length: review.rating}, (_, i) => <DoodleIcon name="star" size={18} key={i}/>)}</div>
            <blockquote>{review.text}</blockquote>
            <figcaption><strong>{review.name}</strong><span>{review.role}</span>{review.sourceUrl && <a className="review-source" href={review.sourceUrl} target="_blank" rel="noopener noreferrer">Отзыв на Stepik ↗</a>}</figcaption>
          </figure>;
        })}
      </div>
    </div>
  </section>;
};
